import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { MemberDetailPanel } from '@components/MemberDetailPanel';
import {
  BrowseToolbar, SpacerRow, interleaveDetail, useBrowseVirtual,
} from '@components/lookup/browse-kit';
import { GridFrame, GridHeadRow, GridToolbar, gridCell } from '@components/grid/CareGrid';
import { useGridView, type GridColumn } from '@components/grid/useGridView';
import { multiSort } from '@lib/grid';
import { SlicerRail } from '@components/lookup/SlicerRail';
import { memberDetail, resultsExport, resultsList } from '@lib/care-api';
import { hayIncludes } from '@lib/hay';
import {
  EMPTY_LOOKUP_FILTERS, activeFilterCount, buildResultsAxes, cohortLabel, matchResultsOps, type LookupFilters,
} from '@lib/lookup-axes';
import { fmtMemberId } from '@lib/member-id';
import type { EmptyReason, MemberDetailResp, ResultsListResp, ResultsRow, ResultsSummary } from '@lib/care-model';
import { EMPTY_REASON_META, emptyRow, emptyTone } from '@lib/care-model';
import { isValidReason, useSessionReason } from '@lib/useSessionReason';

/**
 * ResultsSection - the Results tab (row 418, William 19 Sep 2026: "make this lookup just like the one
 * for the main portal"), the Portal's ResultsSection (v1 MemberLookup.tsx line 4581) on Care's data:
 * one row per RESULTED member, the Portal's columns in the Portal's order: Member, Cohort, City /
 * State, Received, PCP Faxed, Member Letter, Positive Handling, Result, Flag, behind a leading toggle
 * column; a pull card (results ingested, resulted, positive, the value mix); search ("Member ID, name,
 * result value"); the rail; the export of exactly the rows on screen behind the export-scoped
 * re-prompt; a row expands to the member's full record.
 *
 * THIS TAB IS AN OUTCOME DISCLOSURE. His 6 Sep pick (QA-113 option 4) took the outcome off the Member
 * tab's browse row; his 19 Sep word restores this tab beside it, the Portal's shape. The read sits
 * behind the session reason and is audited under its own event (care-results-list) on the server;
 * the browse row on the Member tab stays option 4.
 */

type ColKey = 'member' | 'cohort' | 'location' | 'resulted' | 'pcpFaxed' | 'memberLetter' | 'positive' | 'resultValue' | 'statusFlag';

/** The Results columns in the Portal's order and wording (ResultsSection COLS). */
export const RESULTS_COLUMNS: ReadonlyArray<{ key: ColKey; label: string }> = [
  { key: 'member', label: 'Member' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'location', label: 'City / State' },
  { key: 'resulted', label: 'Received' },
  { key: 'pcpFaxed', label: 'PCP Faxed' },
  { key: 'memberLetter', label: 'Member Letter' },
  { key: 'positive', label: 'Positive Handling' },
  { key: 'resultValue', label: 'Result' },
  { key: 'statusFlag', label: 'Flag' },
];

/** The Portal's Results search: member id, name, result value. */
export function resultsHay(r: ResultsRow): string {
  return `${r.member_id} ${r.full_name} ${r.result_label}`.toLowerCase();
}

function sortValue(r: ResultsRow, key: ColKey): string {
  switch (key) {
    case 'member': return (r.full_name || r.member_id || '').toLowerCase();
    case 'cohort': return (r.cohort || '').toLowerCase();
    case 'location': return `${r.state || ''} ${r.city || ''}`.toLowerCase();
    case 'resulted': return r.resulted_ts || '';
    case 'pcpFaxed': return r.pcp_faxed_ts || '';
    case 'memberLetter': return r.member_letter_ts || '';
    case 'positive': return r.positive_handling_ts || '';
    case 'resultValue': return (r.result_label || '').toLowerCase();
    case 'statusFlag': return (r.status_label || '').toLowerCase();
  }
}

function fmtTsCell(v: string): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(s);
  return m ? `${m[1]} ${m[2]}` : s;
}

const dash = <span className="muted">-</span>;

/** The results pull card: the Portal's ResultsPullBox, aggregates only. */
export function ResultsPullCard({ summary, shown }: { summary: ResultsSummary | null; shown: number }) {
  if (!summary) return null;
  const bars = Object.entries(summary.byResult).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = Math.max(1, ...bars.map((b) => b[1]));
  return (
    <div className="card" data-testid="results-pull" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div style={{ minWidth: 0 }}>
        <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Results data pull</div>
        <div className="small" style={{ marginTop: 2 }}>
          {summary.resultsIngestedAt ? <>Results ingested {fmtTsCell(summary.resultsIngestedAt) || summary.resultsIngestedAt}</> : 'Ingest freshness not recorded for this campaign'}
        </div>
        <div className="micro muted" style={{ marginTop: 2 }}>
          {summary.builtAt ? `Database built ${fmtTsCell(summary.builtAt) || summary.builtAt} (UTC)` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ textAlign: 'center', minWidth: 72 }}>
          <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{summary.resulted.toLocaleString()}</div>
          <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Resulted</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 72 }}>
          <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--crimson)' }}>{summary.positives.toLocaleString()}</div>
          <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Positive</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 72 }}>
          <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{shown.toLocaleString()}</div>
          <div className="micro muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>Shown</div>
        </div>
      </div>
      {bars.length > 0 && (
        <div style={{ display: 'grid', gap: 2, minWidth: 220 }}>
          {bars.map(([label, n]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="micro muted" style={{ width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</span>
              <span style={{ flex: 1, height: 6, background: 'var(--paper-2)', borderRadius: 3, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${Math.max(3, Math.round((n / max) * 100))}%`, background: 'var(--teal)' }} />
              </span>
              <span className="micro muted" style={{ width: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ResultsSectionProps {
  onNeedReason: () => void;
  onRequireExportReason: (proceed: (reason: string) => void) => void;
  onAuditSearch?: (query: string) => void;
  onAuditFilter?: (filters: Record<string, string[]>) => void;
  onJumpToPcp?: (key: string) => void;
}

export function ResultsSection({ onNeedReason, onRequireExportReason, onAuditSearch, onAuditFilter, onJumpToPcp }: ResultsSectionProps) {
  const sessionReason = useSessionReason();
  const reasonValid = isValidReason(sessionReason);

  const [rows, setRows] = useState<ResultsRow[]>([]);
  const [summary, setSummary] = useState<ResultsSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(true);
  const [emptyReason, setEmptyReason] = useState<EmptyReason | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loadedFor, setLoadedFor] = useState('');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState<LookupFilters>(EMPTY_LOOKUP_FILTERS);

  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, MemberDetailResp | 'loading' | 'error'>>({});
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState('');

  useEffect(() => {
    if (!reasonValid || loadedFor === sessionReason) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    void resultsList(sessionReason)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) {
          const d = r.data as ResultsListResp;
          const list = Array.isArray(d.rows) ? d.rows : [];
          setRows(list);
          setSummary(d.summary ?? null);
          setTotal(d.total ?? list.length);
          setAvailable(d.available !== false);
          setEmptyReason(d.emptyReason);
          setLoadedFor(sessionReason);
        } else {
          setLoadErr(`Results load failed (HTTP ${r.status}).`);
        }
        // The load is over on BOTH branches, set HERE rather than in a finally: a finally runs a
        // microtask after the state above, by which time React may have re-run this effect (loadedFor
        // changed) and flipped `cancelled`, leaving `loading` stuck true (found by the sections test).
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadErr('Results load failed.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonValid, sessionReason, loadedFor]);

  useEffect(() => {
    const q = deferredSearch.trim();
    if (q) onAuditSearch?.(q);
  }, [deferredSearch]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtersSeen = useRef(false);
  useEffect(() => {
    if (!filtersSeen.current) {
      filtersSeen.current = true;
      if (activeFilterCount(filters) === 0) return;
    }
    onAuditFilter?.(filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const railFiltered = useMemo(
    () => (Object.keys(filters).length ? rows.filter((r) => matchResultsOps(r, filters)) : rows),
    [rows, filters],
  );
  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return railFiltered;
    return railFiltered.filter((r) => hayIncludes(resultsHay(r), q));
  }, [railFiltered, deferredSearch]);
  const axes = useMemo(() => buildResultsAxes(rows), [rows]);

  const toggleRow = async (memberId: string) => {
    if (openId === memberId) {
      setOpenId(null);
      return;
    }
    setOpenId(memberId);
    if (details[memberId] && details[memberId] !== 'error') return;
    setDetails((prev) => ({ ...prev, [memberId]: 'loading' }));
    try {
      const r = await memberDetail(memberId, sessionReason);
      setDetails((prev) => ({ ...prev, [memberId]: r.ok && (r.data as MemberDetailResp).found ? (r.data as MemberDetailResp) : 'error' }));
    } catch {
      setDetails((prev) => ({ ...prev, [memberId]: 'error' }));
    }
  };

  // The toggle column lives inside a memoized `columns` array (row 356's grid habit), whose deps are
  // the open row and the loaded details, NOT the session reason. A closure captured there before the
  // grant would call the reveal with an EMPTY reason and be refused by the server (found by the shell
  // test on 19 Sep 2026: memberDetail called with ''). The column therefore calls through a ref that
  // always holds the current toggleRow.
  const toggleRef = useRef(toggleRow);
  toggleRef.current = toggleRow;
  const nowrap = { whiteSpace: 'nowrap' } as const;
  const columns = useMemo<GridColumn<ResultsRow>[]>(() => [
    {
      key: 'toggle', label: '', menuLabel: 'Expand', locked: true, stopRowClick: true, defaultWidth: 36,
      thStyle: { width: 36 }, tdStyle: { textAlign: 'center' },
      render: (r) => {
        const open = openId === r.member_id;
        return (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '0 6px' }}
            aria-expanded={open}
            aria-label={open ? 'Hide member detail' : 'Show full member detail'}
            title={open ? 'Hide member detail' : 'Show full member detail'}
            onClick={() => void toggleRef.current(r.member_id)}
          >
            {details[r.member_id] === 'loading' ? '...' : open ? '▾' : '▸'}
          </button>
        );
      },
    },
    {
      key: 'member', label: 'Member', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'member'),
      render: (r) => (
        <>
          <b>{r.full_name || 'member'}</b>
          <span className="muted small" style={{ marginLeft: 6, fontFamily: 'var(--font-mono)' }}>{fmtMemberId(r.member_id)}</span>
        </>
      ),
    },
    { key: 'cohort', label: 'Cohort', tdClassName: 'small', sortValue: (r) => sortValue(r, 'cohort'), render: (r) => (r.cohort ? cohortLabel(r.cohort) : dash) },
    { key: 'location', label: 'City / State', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'location'), render: (r) => [r.city, r.state].filter(Boolean).join(', ') || dash },
    { key: 'resulted', label: 'Received', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'resulted'), render: (r) => fmtTsCell(r.resulted_ts) || dash },
    { key: 'pcpFaxed', label: 'PCP Faxed', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'pcpFaxed'), render: (r) => fmtTsCell(r.pcp_faxed_ts) || dash },
    { key: 'memberLetter', label: 'Member Letter', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'memberLetter'), render: (r) => fmtTsCell(r.member_letter_ts) || dash },
    {
      key: 'positive', label: 'Positive Handling', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'positive'),
      render: (r) => (r.is_positive ? <span style={{ color: 'var(--crimson)', fontWeight: 500 }}>{fmtTsCell(r.positive_handling_ts) || 'pending'}</span> : dash),
    },
    {
      key: 'resultValue', label: 'Result', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'resultValue'),
      render: (r) => (r.result_label ? (r.is_positive ? <span className="pill pill-danger">{r.result_label}</span> : <span className="small">{r.result_label}</span>) : dash),
    },
    { key: 'statusFlag', label: 'Flag', tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => sortValue(r, 'statusFlag'), render: (r) => <span title={r.status_meaning || r.status_label}>{r.status_label || dash}</span> },
  ], [details, openId]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = useGridView('lookup-results', columns, { defaultSort: [{ k: 'resulted', asc: false }] });
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const sorted = useMemo(
    () => multiSort(filtered, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r)),
    [filtered, view.sort, byKey],
  );
  const span = Math.max(1, view.visible.length);
  const displayItems = useMemo(() => interleaveDetail(sorted, (r) => r.member_id === openId), [sorted, openId]);
  const virtual = useBrowseVirtual(displayItems, (it) => `${it.kind}:${it.row.member_id}`, 500);
  const positiveShown = useMemo(() => sorted.reduce((n, r) => n + (r.is_positive ? 1 : 0), 0), [sorted]);

  const runExport = async (exportReason: string) => {
    setExporting(true);
    setExportErr('');
    try {
      const { blob, filename } = await resultsExport(exportReason, sorted);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!reasonValid) {
    return (
      <div className="note" style={{ marginBottom: 14 }}>
        Provide a reason-for-access to load lab results.{' '}
        <button type="button" className="btn btn-sm" onClick={onNeedReason}>Provide reason</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ResultsPullCard summary={summary} shown={sorted.length} />
        <BrowseToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Member ID, name, result value"
          ariaLabel="Search the resulted members"
          shown={sorted.length}
          total={rows.length}
          noun="resulted"
          right={
            <button
              type="button"
              className="btn btn-sm"
              disabled={exporting || sorted.length === 0}
              title="Export the filtered result rows (current view) to Excel"
              onClick={() => {
                setExportErr('');
                onRequireExportReason((exportReason) => { void runExport(exportReason); });
              }}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          }
        />
        {exportErr && <div className="note danger" style={{ marginBottom: 14 }}>{exportErr}</div>}
        {loadErr && <div className="note danger" style={{ marginBottom: 14 }}>{loadErr}</div>}
        {!available && !loading && !loadErr && (
          <div className={`note ${emptyTone(emptyReason)}`} style={{ marginBottom: 14 }}>
            {emptyReason === 'capability-absent'
              ? 'This campaign has no lab results (not part of this program).'
              : EMPTY_REASON_META[emptyReason ?? 'source-unreachable'].notice ?? 'Results unavailable.'}
          </div>
        )}
        {rows.length < total && (
          <div className="muted small" style={{ marginBottom: 8 }}>
            Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()} resulted members (server cap).
          </div>
        )}
        <div className="micro muted" style={{ marginBottom: 6 }}>
          {positiveShown.toLocaleString()} positive of {sorted.length.toLocaleString()} shown
        </div>

        <GridFrame view={view}>
        <GridToolbar view={view} />
        <div ref={virtual.scrollRef} className="card card-flush" style={{ maxHeight: view.expanded ? 'calc(100vh - 90px)' : '65vh', overflow: 'auto' }}>
          <table className="dt">
            <thead>
              <GridHeadRow view={view} />
            </thead>
            <tbody>
              {displayItems.length === 0 && (
                <tr>
                  <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                    {loading ? 'Loading lab results...' : emptyRow(emptyReason, search.trim() ? 'No results match this search.' : 'No resulted members yet for this campaign.')}
                  </td>
                </tr>
              )}
              <SpacerRow height={virtual.padTop} colSpan={span} />
              {virtual.items.map((vi) => {
                const item = displayItems[vi.index];
                if (!item) return null;
                const r = item.row;
                if (item.kind === 'detail') {
                  const d = details[r.member_id];
                  return (
                    <tr key={vi.key} data-index={vi.index} ref={virtual.measureRef}>
                      <td colSpan={span} style={{ background: 'var(--paper-2)', padding: '12px 16px', height: 'auto' }}>
                        {d === 'loading' || d === undefined ? (
                          <span className="muted small">Loading member detail...</span>
                        ) : d === 'error' ? (
                          <span className="muted small">Could not load this member.</span>
                        ) : (
                          <MemberDetailPanel detail={d} onJumpToPcp={onJumpToPcp} />
                        )}
                      </td>
                    </tr>
                  );
                }
                const expanded = openId === r.member_id;
                return (
                  <tr
                    key={vi.key}
                    data-index={vi.index}
                    ref={virtual.measureRef}
                    tabIndex={0}
                    aria-expanded={expanded}
                    aria-label={`${r.full_name || 'member'} ${fmtMemberId(r.member_id)}`}
                    onClick={() => void toggleRow(r.member_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void toggleRow(r.member_id);
                      }
                    }}
                    style={{ cursor: 'pointer', background: expanded ? 'var(--paper-2)' : undefined }}
                  >
                    {view.visible.map((c) => (
                      <td key={c.key} {...gridCell(view, c, r)}>
                        {c.render(r, vi.index)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <SpacerRow height={virtual.padBottom} colSpan={span} />
            </tbody>
          </table>
        </div>
        </GridFrame>
      </div>
      <SlicerRail
        axes={axes}
        filters={filters}
        onFilters={setFilters}
        scope={{ shown: railFiltered.length, total: rows.length }}
        noun="resulted"
      />
    </div>
  );
}
